// apps/web/src/components/common/Header.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Badge,
  Chip,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  FitnessCenter as FitnessCenterIcon,
  History as HistoryIcon,
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { blue, indigo } from '@mui/material/colors';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // Используем lg для схлопывания
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const burgerButtonRef = useRef<HTMLButtonElement>(null);

  const menuItems = [
    { label: 'Главная', icon: <PeopleIcon />, path: '/' },
    { label: 'Дашборд', icon: <DashboardIcon />, path: '/dashboard' },
    { label: 'Тренировки', icon: <FitnessCenterIcon />, path: '/workouts', comingSoon: true },
    { label: 'Посещения', icon: <HistoryIcon />, path: '/checkins', comingSoon: false },
    { label: 'Платежи', icon: <PaymentIcon />, path: '/payments', comingSoon: true },
    { label: 'Настройки', icon: <SettingsIcon />, path: '/settings', comingSoon: true },
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    if (!mobileOpen) {
      burgerButtonRef.current?.blur(); // снимаем фокус перед открытием
    }
    setMobileOpen(!mobileOpen);
  };

  // Управление фокусом для предотвращения конфликта aria-hidden
  useEffect(() => {
    if (mobileOpen) {
      const timer = setTimeout(() => {
        const drawerPaper = document.querySelector('.MuiDrawer-paper');
        if (drawerPaper) {
          const focusable = drawerPaper.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (focusable && focusable instanceof HTMLElement) {
            focusable.focus();
          } else if (drawerPaper instanceof HTMLElement) {
            drawerPaper.focus();
          }
        }
      }, 150);
      return () => clearTimeout(timer);
    } else {
      if (burgerButtonRef.current) {
        burgerButtonRef.current.focus();
      }
    }
  }, [mobileOpen]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    handleMenuClose();
    if (mobileOpen) setMobileOpen(false);
  };

  const getUserInfo = () => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  };

  const user = getUserInfo();
  const userInitial = user?.email?.[0]?.toUpperCase() || 'A';

  const drawer = (
    <Box sx={{ width: 280 }}>
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          sx={{
            bgcolor: theme.palette.primary.main,
            width: 48,
            height: 48,
          }}
        >
          {userInitial}
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight="medium">
            {user?.email || 'Администратор'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Администратор
          </Typography>
        </Box>
      </Box>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              onClick={handleDrawerToggle}
              disabled={item.comingSoon}
              selected={location.pathname === item.path}
              sx={{
                py: 1.5,
                px: 3,
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  color: theme.palette.primary.main,
                  '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.12)' },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: location.pathname === item.path ? theme.palette.primary.main : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    {item.label}
                    {item.comingSoon && (
                      <Chip
                        label="Скоро"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          backgroundColor: theme.palette.grey[200],
                          color: theme.palette.grey[600],
                        }}
                      />
                    )}
                  </Box>
                }
              />
              {item.comingSoon && <ChevronRightIcon fontSize="small" />}
            </ListItemButton>
          </ListItem>
        ))}
        <Divider sx={{ my: 1 }} />
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              py: 1.5,
              px: 3,
              color: theme.palette.error.main,
              '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.04)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Выйти" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
          backdropFilter: 'blur(20px)',
          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.8),
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 3 } }}>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              ref={burgerButtonRef}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              mr: 4,
            }}
          >
            <FitnessCenterIcon
              sx={{
                mr: 1,
                color: theme.palette.primary.main,
                fontSize: 32,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg, ${indigo[700]}, ${blue[400]})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              GymFlow
            </Typography>
          </Box>

          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.label}
                  component={RouterLink}
                  to={item.path}
                  startIcon={item.icon}
                  disabled={item.comingSoon}
                  sx={{
                    color:
                      location.pathname === item.path
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                    backgroundColor:
                      location.pathname === item.path
                        ? alpha(theme.palette.primary.main, 0.08)
                        : 'transparent',
                    '&:hover': {
                      color: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                    minWidth: 'auto',
                    px: 2,
                  }}
                >
                  {item.label}
                  {item.comingSoon && (
                    <Chip
                      label="Скоро"
                      size="small"
                      sx={{
                        ml: 1,
                        height: 20,
                        fontSize: '0.65rem',
                        backgroundColor: theme.palette.grey[200],
                        color: theme.palette.grey[600],
                      }}
                    />
                  )}
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <IconButton color="inherit" sx={{ mr: 1 }}>
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <IconButton
              onClick={handleMenuOpen}
              sx={{
                p: 0.5,
                border: `2px solid ${theme.palette.primary.light}`,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: theme.palette.primary.main,
                }}
              >
                {userInitial}
              </Avatar>
            </IconButton>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{ sx: { width: 240, mt: 1.5 } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="medium">
                {user?.email || 'Администратор'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Администратор
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Профиль
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Настройки
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              <Typography color="error">Выйти</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer рендерится только на мобильных экранах (isMobile) */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: false, // не держим в DOM постоянно
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              outline: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}
    </>
  );
};

export default Header;
